import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useTheme } from 'styled-components';
import ActionIcon from '../ActionIcon';
import ModalOverlay from '../ModalOverlay';
import { tokens } from '../tokens';
import useIsMobile from '../useIsMobile';

// Right-hand side panel on ModalOverlay (z 1000). Desktop: pinned to the
// right edge at `width` (capped to the viewport). Below 768px it fills the
// screen. Header = optional orange eyebrow, title, subtitle and a close
// button; the body scrolls; `footer` stays pinned to the bottom.

let nextId = 0;

const Drawer = ({
  open,
  onClose,
  title,
  subtitle,
  eyebrow,
  children,
  footer,
  width,
  zIndex,
  initialFocusRef,
}) => {
  const t = tokens(useTheme());
  const isMobile = useIsMobile();
  const idRef = useRef(null);
  if (!idRef.current) {
    nextId += 1;
    idRef.current = `v3-drawer-${nextId}`;
  }
  const titleId = `${idRef.current}-title`;

  return (
    <ModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={title ? titleId : undefined}
      label={title ? undefined : 'Panel'}
      placement={isMobile ? 'full' : 'right'}
      zIndex={zIndex}
      initialFocusRef={initialFocusRef}
      panelStyle={{
        display: 'flex',
        flexDirection: 'column',
        width: isMobile ? '100%' : `min(${width}px, 100vw)`,
        height: '100%',
        maxHeight: '100vh',
        background: t.card,
        color: t.text,
        borderLeft: isMobile ? 'none' : '1px solid ' + t.border,
        boxShadow: t.shadowPop,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: isMobile ? '14px 12px 12px 16px' : '20px 16px 16px 22px',
          borderBottom: '1px solid ' + t.border,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {eyebrow ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                color: t.primary,
                marginBottom: 6,
              }}
            >
              {eyebrow}
            </div>
          ) : null}
          {title ? (
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
                lineHeight: 1.25,
                color: t.text,
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </h2>
          ) : null}
          {subtitle ? (
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: t.sub,
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        <ActionIcon icon="x" title="Close" onClick={onClose} />
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: isMobile ? 16 : '20px 22px',
        }}
      >
        {children}
      </div>
      {footer ? (
        <div
          style={{
            flexShrink: 0,
            padding: isMobile
              ? '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))'
              : '14px 22px',
            borderTop: '1px solid ' + t.border,
            background: t.head,
          }}
        >
          {footer}
        </div>
      ) : null}
    </ModalOverlay>
  );
};

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  eyebrow: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  width: PropTypes.number,
  zIndex: PropTypes.number,
  initialFocusRef: PropTypes.shape({ current: PropTypes.any }),
};

Drawer.defaultProps = {
  title: null,
  subtitle: null,
  eyebrow: null,
  children: null,
  footer: null,
  width: 440,
  zIndex: 1000,
  initialFocusRef: undefined,
};

export default Drawer;
